import cola from './vendor/cola.cjs'
import { nodeWidth } from './node-display.mjs'

const GAP = 16
const PADDING = 16
const HEADER = 32

// Same projection as Layout Lab: choose one separation axis for each pair,
// then solve the minimum-displacement constraints on both axes. No grid fallback.
function projectRectangles(nodes) {
  const constraints = { x: [], y: [] }
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const a = nodes[i],
        b = nodes[j]
      const dx = Math.abs(a.x - b.x) / ((a.width + b.width) / 2 + GAP)
      const dy = Math.abs(a.y - b.y) / ((a.height + b.height) / 2 + GAP)
      const axis = dx >= dy ? 'x' : 'y'
      constraints[axis].push(a[axis] <= b[axis] ? [i, j] : [j, i])
    }
  }
  for (const axis of ['x', 'y']) {
    const dimension = axis === 'x' ? 'width' : 'height'
    const variables = nodes.map((n) => new cola.Variable(n[axis]))
    const separation = constraints[axis].map(
      ([i, j]) =>
        new cola.Constraint(
          variables[i],
          variables[j],
          (nodes[i][dimension] + nodes[j][dimension]) / 2 + GAP + 0.001,
        ),
    )
    if (!separation.length) continue
    new cola.Solver(variables, separation).solve()
    variables.forEach((v, i) => {
      nodes[i][axis] = v.position()
    })
  }
}

// Solve in paragraph-local coordinates; the parent envelope supplies containment
// and a reserved title band. Only real, internal unit relations supply springs.
export function layoutUnits(units, edges, labels, minWidth) {
  const nodes = units.map((unit, i) => ({
    ...unit,
    label: labels.get(unit.id),
    width: nodeWidth(labels.get(unit.id), true),
    height: 29,
    x: Math.cos(i * 2.399963229728653) * 55 * Math.sqrt(i),
    y: Math.sin(i * 2.399963229728653) * 55 * Math.sqrt(i),
  }))
  if (!nodes.length) return { nodes, width: Math.max(142, minWidth), height: 64 }
  const indices = new Map(nodes.map((n, i) => [n.id, i]))
  const links = edges
    .filter(
      (e) => e.level === 'unit' && e.source !== e.target && indices.has(e.source) && indices.has(e.target),
    )
    .map((e) => ({ source: indices.get(e.source), target: indices.get(e.target) }))
  const length = ({ source, target }) =>
    45 +
    (Math.hypot(nodes[source].width, nodes[source].height) +
      Math.hypot(nodes[target].width, nodes[target].height)) /
      4

  if (nodes.length > 1) {
    if (links.length) {
      const distances = new cola.Calculator(
        nodes.length,
        links,
        (e) => e.source,
        (e) => e.target,
        length,
      ).DistanceMatrix()
      const seed = new cola.Descent([nodes.map((n) => n.x), nodes.map((n) => n.y)], distances)
      seed.run(45)
      nodes.forEach((n, i) => {
        n.x = seed.x[0][i]
        n.y = seed.x[1][i]
      })
    }
    projectRectangles(nodes)
    const distances = cola.Descent.createSquareMatrix(nodes.length, (i, j) => (i === j ? 0 : Infinity))
    const weights = cola.Descent.createSquareMatrix(nodes.length, () => 0)
    links.forEach((e) => {
      distances[e.source][e.target] = distances[e.target][e.source] = length(e)
      weights[e.source][e.target] = weights[e.target][e.source] = 1
    })
    const descent = new cola.Descent([nodes.map((n) => n.x), nodes.map((n) => n.y)], distances, weights)
    for (let pass = 0; pass < 120; pass++) {
      descent.x = [nodes.map((n) => n.x), nodes.map((n) => n.y)]
      descent.computeDerivatives(descent.x)
      const cx = nodes.reduce((sum, n) => sum + n.x, 0) / nodes.length
      const cy = nodes.reduce((sum, n) => sum + n.y, 0) / nodes.length
      nodes.forEach((n, i) => {
        const stiffness = weights[i].reduce((sum, w, j) => sum + (w ? (2 * w) / distances[i][j] ** 2 : 0), 0)
        const rate = Math.min(1400, 0.2 / Math.max(stiffness, 1e-9))
        const dx = -descent.g[0][i] * rate + (cx - n.x) * 0.009
        const dy = -descent.g[1][i] * rate + (cy - n.y) * 0.009
        const scale = Math.min(1, 5 / Math.max(Math.hypot(dx, dy), 1e-9))
        n.x += dx * scale
        n.y += dy * scale
      })
      projectRectangles(nodes)
    }
  }

  const left = Math.min(...nodes.map((n) => n.x - n.width / 2))
  const right = Math.max(...nodes.map((n) => n.x + n.width / 2))
  const top = Math.min(...nodes.map((n) => n.y - n.height / 2))
  const bottom = Math.max(...nodes.map((n) => n.y + n.height / 2))
  nodes.forEach((n) => {
    n.x -= (left + right) / 2
    n.y += HEADER / 2 - (top + bottom) / 2
  })
  return {
    nodes,
    width: Math.max(142, minWidth, right - left + PADDING * 2),
    height: bottom - top + PADDING * 2 + HEADER,
  }
}
